import type { CadDocument, Layer } from '../model/document';
import type { CadEntity } from '../model/entity';
import type { CadOp } from './operation';

/**
 * 레이어 편집 (작업지시서 P4-01)
 *
 * ── 왜 순수 함수로 빼는가 ───────────────────────────────────────────────
 *
 *   레이어 삭제는 **도면을 조용히 망가뜨릴 수 있는** 몇 안 되는 조작이다.
 *   존재하지 않는 레이어를 가리키는 객체는 화면에서 사라지고,
 *   사용자는 지워진 줄 알고 다시 그린다. UI 안에 로직을 두면 검증할 수
 *   없으므로 여기서 순수 함수로 만들고 테스트로 못 박는다.
 *
 *   모든 함수는 실패 이유를 문자열로 돌려준다. 예외를 던지면
 *   패널이 통째로 죽어 도면 작업이 끊긴다.
 */

export interface LayerEditResult {
  /** 적용할 연산. 실패하면 비어 있다. */
  ops: CadOp[];
  /** 사용자에게 보여줄 실패 이유. 성공이면 null */
  error: string | null;
  /** 실패는 아니지만 알려야 할 것 (예: 객체 N개가 옮겨졌다) */
  notice?: string;
}

const ok = (ops: CadOp[], notice?: string): LayerEditResult => ({ ops, error: null, notice });
const fail = (error: string): LayerEditResult => ({ ops: [], error });

/** 레이어 코드 규칙 — DXF 가 허용하지 않는 문자를 미리 막는다 */
const CODE_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,30}$/;

export function validateLayerCode(code: string, existing: Layer[]): string | null {
  const c = code.trim();
  if (c === '') return '레이어 코드를 입력하세요.';
  if (!CODE_RE.test(c)) {
    return '영문·숫자·하이픈·밑줄만 쓸 수 있고 31자를 넘을 수 없습니다. (DXF 제약)';
  }
  // 대소문자만 다른 코드는 DXF 왕복에서 충돌한다
  if (existing.some((l) => l.code.toLowerCase() === c.toLowerCase())) {
    return `이미 있는 레이어입니다: ${c}`;
  }
  return null;
}

/* -------------------------------------------------------------------------- *
 * 추가
 * -------------------------------------------------------------------------- */

export function addLayer(doc: CadDocument, input: Partial<Layer> & { code: string }): LayerEditResult {
  const code = input.code.trim();
  const bad = validateLayerCode(code, doc.layers);
  if (bad) return fail(bad);

  const before = doc.layers.map((l) => ({ ...l }));
  const maxOrder = before.reduce((m, l) => Math.max(m, l.sortOrder), 0);

  const layer: Layer = {
    code,
    name: (input.name ?? code).trim() || code,
    color: input.color ?? '#D9D9D9',
    lineType: input.lineType ?? 'continuous',
    lineWeight: input.lineWeight ?? 25,
    visible: input.visible ?? true,
    locked: input.locked ?? false,
    printable: input.printable ?? true,
    sortOrder: input.sortOrder ?? maxOrder + 10,
  };

  return ok([{ kind: 'layer', command: 'LAYER', before, after: [...before, layer] }]);
}

/* -------------------------------------------------------------------------- *
 * 수정
 * -------------------------------------------------------------------------- */

export function updateLayer(
  doc: CadDocument,
  code: string,
  change: Partial<Omit<Layer, 'code'>>
): LayerEditResult {
  if (!doc.layers.some((l) => l.code === code)) return fail(`없는 레이어입니다: ${code}`);

  const before = doc.layers.map((l) => ({ ...l }));
  const after = before.map((l) => (l.code === code ? { ...l, ...change } : l));
  return ok([{ kind: 'layer', command: 'LAYER', before, after }]);
}

/**
 * 레이어 코드를 바꾼다.
 *
 * ★ 그 레이어에 놓인 객체도 **같은 연산 묶음 안에서** 함께 옮겨야 한다.
 *   나눠서 처리하면 Undo 를 한 번 눌렀을 때 레이어만 되돌아가고
 *   객체는 없는 레이어를 가리킨 채 남는다 — 화면에서 사라진다.
 */
export function renameLayerCode(doc: CadDocument, from: string, to: string): LayerEditResult {
  const target = doc.layers.find((l) => l.code === from);
  if (!target) return fail(`없는 레이어입니다: ${from}`);

  const next = to.trim();
  if (next === from) return ok([]);

  const bad = validateLayerCode(next, doc.layers.filter((l) => l.code !== from));
  if (bad) return fail(bad);

  const before = doc.layers.map((l) => ({ ...l }));
  const after = before.map((l) => (l.code === from ? { ...l, code: next } : l));

  const ops: CadOp[] = [{ kind: 'layer', command: 'LAYER', before, after }];

  const moved = doc.entities.filter((e) => e.layer === from);
  if (moved.length > 0) {
    ops.push({
      kind: 'update',
      command: 'LAYER',
      before: moved,
      after: moved.map((e) => ({ ...e, layer: next })),
    });
  }

  return ok(ops, moved.length > 0 ? `객체 ${moved.length}개가 함께 옮겨졌습니다.` : undefined);
}

/* -------------------------------------------------------------------------- *
 * 삭제
 * -------------------------------------------------------------------------- */

export interface DeleteLayerOptions {
  /**
   * 그 레이어에 객체가 있을 때 어디로 옮길지.
   * 지정하지 않으면 삭제를 거부한다 — 말없이 객체를 지우면 안 된다.
   */
  moveTo?: string;
}

export function deleteLayer(
  doc: CadDocument,
  code: string,
  options: DeleteLayerOptions = {}
): LayerEditResult {
  if (!doc.layers.some((l) => l.code === code)) return fail(`없는 레이어입니다: ${code}`);
  if (doc.layers.length <= 1) return fail('마지막 레이어는 지울 수 없습니다.');

  const occupants = doc.entities.filter((e) => e.layer === code);

  let ops: CadOp[] = [];
  let notice: string | undefined;

  if (occupants.length > 0) {
    // ★ 객체를 조용히 지우지 않는다. 옮길 곳을 반드시 받는다.
    if (!options.moveTo) {
      return fail(
        `이 레이어에 객체 ${occupants.length}개가 있습니다. 옮길 레이어를 먼저 고르세요.`
      );
    }
    if (options.moveTo === code) return fail('옮길 레이어가 지울 레이어와 같습니다.');
    if (!doc.layers.some((l) => l.code === options.moveTo)) {
      return fail(`없는 레이어입니다: ${options.moveTo}`);
    }

    ops.push({
      kind: 'update',
      command: 'LAYER',
      before: occupants,
      after: occupants.map<CadEntity>((e) => ({ ...e, layer: options.moveTo! })),
    });
    notice = `객체 ${occupants.length}개를 ${options.moveTo} 로 옮겼습니다.`;
  }

  const before = doc.layers.map((l) => ({ ...l }));
  ops = [...ops, { kind: 'layer', command: 'LAYER', before, after: before.filter((l) => l.code !== code) }];

  return ok(ops, notice);
}

/** 레이어에 놓인 객체 수 — 삭제 전 확인 화면이 쓴다 */
export function countEntitiesOnLayer(doc: CadDocument, code: string): number {
  let n = 0;
  for (const e of doc.entities) if (e.layer === code) n++;
  return n;
}
